import React from 'react';
import type {FormDefinitionSimpleResponse} from 'src/api/ProactApiClient';
import {CreateButton} from 'src/design/molecules/CreateButton';
import {FormListMeta} from 'src/pages/Modules/Forms/formHelpers';
import {DataHeader} from 'src/design/organisms/DataHeader';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {
  useGetFormDefinitionCategoryCountsQuery,
  useGetFormDefinitionListQuery,
} from 'src/redux/queries/formDefinition';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useDebounce from 'src/shared/hooks/useDebounce';
import {MyFormsCategoryCards} from './MyFormsCategoryCards';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const maxPageRows = 10;

interface UserFormDefinitionsFilters {
  categoryId: number | null;
}

export const ListUserFormDefinitions: React.FC = () => {
  const navigate = useAppNavigate();
  const {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState<UserFormDefinitionsFilters>({
    identifier: 'my-form-definitions',
    pageSize: maxPageRows,
    defaultFilters: {categoryId: null},
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const {data: categoryCounts} = useGetFormDefinitionCategoryCountsQuery();
  // A stored category can outlive its delete; without its card the filter could never be cleared.
  const storedCategoryId = filters.categoryId ?? undefined;
  const categoryId =
    categoryCounts &&
    !categoryCounts.categories.some(c => c.id === storedCategoryId)
      ? undefined
      : storedCategoryId;
  const {data, isLoading} = useGetFormDefinitionListQuery({
    start,
    length,
    search: debouncedSearchTerm,
    active: true,
    categoryId,
  });

  const indexListData = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;

  const handleNavigateToForm = (id: string | number) =>
    navigate(PathUtility.FormsList(id));

  const handleSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {value} = e.target;
    setSearchTerm(value);
  };

  const handleSelectCategory = (id: number | undefined) => {
    setFilters({categoryId: id ?? null});
  };

  const handleNavigateToAddNew = (id: string | number) =>
    navigate(PathUtility.FormSubmit(id));

  return (
    <>
      <MyFormsCategoryCards
        counts={categoryCounts}
        activeCategoryId={categoryId}
        onSelectCategory={handleSelectCategory}
      />
      <DataHeader
        titleText=""
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTerm}
        isFilterActive={false}
      />
      <DataList
        isLoading={isLoading}
        data={indexListData}
        meta={FormListMeta}
        currentPage={currentPage}
        totalRecords={totalRecords}
        recordsPerPage={maxPageRows}
        onRowClick={handleNavigateToForm}
        onPageChange={handlePageChange}
        hasSearchTerm={true}
        onRowTextRender={(item: FormDefinitionSimpleResponse) =>
          item.uniqueIdentifier
            ? `${item.uniqueIdentifier} - ${item.name}`
            : item.name
        }
        onRenderBeforeRowNode={(item: FormDefinitionSimpleResponse) => (
          <CreateButton
            size="sm"
            onClick={() => handleNavigateToAddNew(item.id)}>
            New
          </CreateButton>
        )}
      />
    </>
  );
};
